import {
  DialogTitle,
  Field,
  Input,
  Label,
  Switch,
  Textarea,
} from "@headlessui/react"
import { useForm } from "@tanstack/react-form"
import { useNavigate, useSearch } from "@tanstack/react-router"
import React, { useState } from "react"

import Button from "@/components/Button"
import FieldInfo from "@/components/FieldInfo"
import FormErrors from "@/components/FormErrors"
import Modal from "@/components/Modal"
import PartnerSelect from "@/components/PartnerSelect"
import SubscriberSelect from "@/components/SubscriberSelect"
import { useAuth } from "@/contexts/auth"
import {
  useCreateCertificate,
  useDeleteCertificate,
  useUpdateCertificate,
} from "@/hooks"
import { Route } from "@/routes/_auth.administration/certificates/index"
import type { X509Certificate } from "@/services/partnerApi"
import { formError } from "@/utils/forms"
import { hasPermission } from "@/utils/rbac"

type Props =
  | {
      cert?: X509Certificate
      create: true
    }
  | {
      cert: X509Certificate
      create: false
    }

const CertificateForm: React.FC<Props> = ({ cert, create }) => {
  const { metadataPublic } = useAuth()
  const { partnerId } = useSearch({ strict: false })
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const navigate = useNavigate()
  const goBack = () =>
    navigate({
      to: Route.to,
      search: (prev) => prev,
    })

  const createMutation = useCreateCertificate()
  const updateMutation = useUpdateCertificate()
  const deleteMutation = useDeleteCertificate()

  const form = useForm({
    defaultValues: {
      // form values for creating
      subscriber_id: "",
      enabled: true,
      name: "",
      comment: "",
      ...(create ? { certificate: "" } : {}), // only add certificate to defaultValues if create=true
      // If a certificate (cert) is passed into the component (only for editing), overwrite previous values
      ...(!create ? { ...cert } : {}),
    },
    // `formApi` rather than the `form` binding: reading `form` from inside its
    // own initializer makes v1's generics circular, and tsc then reports the
    // form as two unrelated types with the same name.
    onSubmit: ({ value, formApi }) => {
      if (create) {
        return createMutation.mutate(
          {
            certificate: value.certificate!,
            comment: value.comment,
            enabled: value.enabled,
            name: value.name,
            subscriber_id: value.subscriber_id,
          },
          {
            onSuccess: () => {
              return goBack()
            },
            onError: (err) =>
              formApi.setErrorMap({ onChange: formError(err.message) }),
          },
        )
      } else {
        return updateMutation.mutate(
          {
            comment: value.comment,
            enabled: value.enabled,
            id: value.id!,
            name: value.name,
          },
          {
            onSuccess: goBack,
            onError: (err) =>
              formApi.setErrorMap({ onChange: formError(err.message) }),
          },
        )
      }
    },
    validators: {
      onSubmit: ({ value }) => {
        if (!hasPermission(metadataPublic, "write")) {
          return "You do not have permission to manage subscribers"
        }

        if (value.certificate?.trim() === "") {
          return "No Certificate selected."
        }

        return null
      },
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
    >
      {hasPermission(metadataPublic, "managePartners") && create && (
        <div className="max-w-md bg-white p-4 mb-5 rounded shadow">
          <Field>
            <Label className="block text-sm text-gray-500 mb-2">
              Partner Name
            </Label>
            <PartnerSelect
              disabled={!create}
              defaultDisabled={true}
              defaultOption="Select a Partner"
              defaultValue=""
              id="partnerId"
              name="partnerId"
              onChange={(e) => {
                navigate({
                  to: "/administration/certificates/create",
                  search: (prev: any) => ({
                    ...prev,
                    subscriberId: undefined,
                    partnerId:
                      e.target.value === "" ? undefined : e.target.value,
                  }),
                })
                form.setFieldValue("subscriber_id", "", {
                  dontUpdateMeta: false,
                })
                // v1 derives `isPristine` and `errors`, and wants a complete
                // meta object including private counters, so spread the
                // previous one rather than replacing it wholesale.
                //
                // errorMap empty, not seeded with "Select a subscriber". v0
                // stored `errors` and this call set it to [], so the message it
                // wrote here never rendered; v1 derives `errors` from errorMap,
                // so keeping it would paint the field red the moment you switch
                // partner. Clearing matches what v0 displayed.
                form.setFieldMeta("subscriber_id", (prev) => ({
                  ...prev,
                  isTouched: false,
                  isBlurred: false,
                  isDirty: true,
                  isValidating: false,
                  errorMap: {},
                }))
              }}
              value={partnerId ?? metadataPublic.org_id}
            />
          </Field>
        </div>
      )}

      <div className="max-w-md bg-white p-4 rounded shadow">
        <form.Field
          name="subscriber_id"
          validators={{
            onChange: ({ value }) => {
              return !value ? "A subscriber is required" : undefined
            },
          }}
          children={(field) => {
            return (
              <Field className="mb-5">
                <Label
                  htmlFor={field.name}
                  className="block text-sm text-gray-500 mb-2"
                >
                  Subscriber Name
                </Label>

                <SubscriberSelect
                  defaultDisabled={false}
                  defaultOption="Select a Subscriber"
                  disabled={!create}
                  hasError={field.state.meta.errors.length > 0}
                  id={field.name}
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  value={field.state.value}
                />
                <FieldInfo field={field} />
              </Field>
            )
          }}
        />

        <form.Field
          name="name"
          validators={{
            onBlur: ({ value }) => {
              return !value
                ? "A name is required"
                : value.length < 3
                  ? "Name must be at least 3 characters"
                  : undefined
            },
          }}
          children={(field) => {
            return (
              <Field className="mb-5">
                <Label
                  htmlFor={field.name}
                  className="block text-sm text-gray-500 mb-2"
                >
                  Certificate Name
                </Label>
                <Input
                  autoComplete="off"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  id={field.name}
                  name={field.name}
                  onBlur={() => {
                    form.setFieldValue("name", field.state.value.trim())
                    return field.handleBlur()
                  }}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="example-subscriber"
                  value={field.state.value}
                />
                <FieldInfo field={field} />
              </Field>
            )
          }}
        />

        <form.Field
          name="enabled"
          children={(field) => {
            return (
              <Field className="mb-5">
                <Label
                  htmlFor={field.name}
                  className="block text-sm text-gray-500 mb-2"
                >
                  Certificate Enabled
                </Label>
                <div className="flex items-center h-100">
                  <Switch
                    checked={field.state.value}
                    onChange={() => field.handleChange(!field.state.value)}
                    className="group inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition data-[checked]:bg-green-600"
                  >
                    <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
                  </Switch>
                  <span className="ml-2">
                    {field.state.value ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <FieldInfo field={field} />
              </Field>
            )
          }}
        />

        {create ? (
          <form.Field
            name="certificate"
            validators={{
              // v1 types a field's errorMap slots from that field's own
              // validators, so a slot with no validator behind it accepts
              // nothing -- the PEM check has to live here rather than being
              // pushed into the slot from the FileReader callback. Doing it
              // here also clears the error when a later, valid file is picked,
              // which the old setErrorMap call never did.
              onChange: ({ value }) =>
                !value ||
                (value.includes("-----BEGIN CERTIFICATE-----") &&
                  value.includes("-----END CERTIFICATE-----"))
                  ? undefined
                  : "Invalid certificate format.  Please upload a valid certificate file.",
            }}
            children={(field) => {
              return (
                <Field className="mb-5">
                  <Label
                    htmlFor={field.name}
                    className="block text-sm text-gray-500 mb-2"
                  >
                    Certificate File
                  </Label>
                  <Input
                    accept=".crt,.pem,.cer"
                    className="w-full text-gray-8500 font-medium text-sm bg-gray-100 file:cursor-pointer cursor-pointer file:border-0 file:py-2 file:px-4 file:mr-4 file:bg-gray-300 file:hover:bg-gray-400 file:text-white rounded"
                    type="file"
                    onChange={(event) => {
                      const { files } = event.target

                      if (!files || files.length === 0) {
                        return
                      }

                      const file = files[0]

                      const reader = new FileReader()
                      reader.readAsText(file)

                      // Error handler to log file reading errors
                      reader.onerror = function () {
                        console.error("Error reading file:", reader.error)
                      }

                      // Success handler to read the file contents
                      reader.onload = async function (e) {
                        const content = e.target?.result as string

                        field.handleChange(content)
                      }
                    }}
                  />
                  <FieldInfo field={field} />
                </Field>
              )
            }}
          />
        ) : (
          <>
            <Field className="mb-5">
              <Label className="block text-sm text-gray-500 mb-2">
                Certificate Fingerprint
              </Label>
              <Input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                disabled={true}
                type="text"
                value={cert.sha256_fingerprint}
              />
            </Field>
            <Field className="mb-5">
              <Label className="block text-sm text-gray-500 mb-2">
                Valid From
              </Label>
              <Input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                disabled={true}
                type="text"
                value={cert.not_before}
              />
            </Field>
            <Field className="mb-5">
              <Label className="block text-sm text-gray-500 mb-2">
                Valid Until
              </Label>
              <Input
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                disabled={true}
                type="text"
                value={cert.not_after}
              />
            </Field>
          </>
        )}

        <form.Field
          name="comment"
          children={(field) => {
            return (
              <Field className="mb-5">
                <Label
                  htmlFor={field.name}
                  className="block text-sm text-gray-500 mb-2"
                >
                  Comments
                </Label>
                <Textarea
                  autoComplete="off"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  id={field.name}
                  name={field.name}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  value={field.state.value}
                />
                <FieldInfo field={field} />
              </Field>
            )
          }}
        />

        <form.Subscribe
          selector={(state) => [
            state.errors,
            state.canSubmit,
            state.isSubmitting,
          ]}
          children={([errors, canSubmit, isSubmitting]) => (
            <>
              <FormErrors errors={errors} />

              <div className="flex justify-end gap-4">
                {create ? (
                  <Button
                    type="reset"
                    color="reset"
                    onClick={(e) => {
                      e.preventDefault()
                      return form.reset()
                    }}
                  >
                    Reset
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    color="danger"
                    disabled={!canSubmit}
                    onClick={(e) => {
                      e.preventDefault()
                      if (!hasPermission(metadataPublic, "delete")) {
                        return form.setErrorMap({
                          onSubmit: formError(
                            "You do not have permission to delete subscribers",
                          ),
                        })
                      }

                      return setShowDeleteModal(true)
                    }}
                  >
                    Delete
                  </Button>
                )}
                <Button type="submit" disabled={!canSubmit} color="blue">
                  {isSubmitting ? "..." : create ? "Submit" : "Update"}
                </Button>
              </div>
            </>
          )}
        />
      </div>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <DialogTitle className="font-bold">Delete Certificate</DialogTitle>
        <p>Are you sure you want to permanently delete this certificate?</p>
        <div className="flex items-center justify-end rounded-b gap-2">
          <Button
            color="reset"
            type="button"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            type="submit"
            onClick={() =>
              deleteMutation.mutate(cert!, {
                onSuccess: () => {
                  return goBack()
                },
                onError: (err) =>
                  form.setErrorMap({ onChange: formError(err.message) }),
              })
            }
          >
            Delete
          </Button>
        </div>
      </Modal>
    </form>
  )
}

export default CertificateForm
